/**
 * Typed slice store — phase 4.0 of the decomposition (see DESIGN-phase4.md).
 *
 * Deliberately tiny (~150 lines, zero dependencies): slices of serializable
 * domain facts, shallow-merge updates, microtask-batched subscriptions, and
 * a per-slice monotonic generation that replaces hand-rolled invalidation
 * counters like index.html's `state.loadGeneration`.
 *
 * Hard rules (from the design):
 *  - Only HUMAN-SPEED facts live here. Per-tick data (candles, ltp) and
 *    live handles (charts, sockets, timers) never do.
 *  - Subscribers are notified once per microtask burst, with the final
 *    value — no synchronous re-entrancy. A subscriber that keeps causing
 *    further updates trips a loud cascade cap instead of looping forever.
 */

export interface Store<S extends object> {
  /** Current value of a slice. Treat as read-only — mutate via update(). */
  get<K extends keyof S>(slice: K): S[K];
  /** Shallow-merge a patch; notifies subscribers unless nothing changed. */
  update<K extends keyof S>(slice: K, patch: Partial<S[K]>): void;
  /** Replace a slice wholesale (e.g. adopting server state). */
  set<K extends keyof S>(slice: K, value: S[K]): void;
  /** Post-hoc, microtask-batched. Returns an unsubscribe function. */
  subscribe<K extends keyof S>(slice: K, fn: (value: S[K], prev: S[K]) => void): () => void;
  /**
   * Monotonic per-slice generation. Bumps on every effective update/set/
   * touch. Async flows capture it before a fetch and compare after — the
   * store-native replacement for hand-rolled load counters.
   */
  generation(slice: keyof S): number;
  /**
   * Bump a slice's generation WITHOUT changing data or notifying — for
   * "a new load supersedes any in-flight one" when the facts themselves
   * are unchanged (e.g. reloading the same symbol).
   */
  touch(slice: keyof S): void;
}

const CASCADE_CAP = 10;

export function createStore<S extends object>(initial: S): Store<S> {
  const values = new Map<keyof S, S[keyof S]>();
  const generations = new Map<keyof S, number>();
  const subscribers = new Map<keyof S, Set<(v: any, prev: any) => void>>();
  // slice -> value at the START of the current batch (what subscribers
  // will receive as `prev`).
  const pendingPrev = new Map<keyof S, S[keyof S]>();
  let flushQueued = false;
  let cascadeDepth = 0;

  for (const k of Object.keys(initial) as (keyof S)[]) {
    values.set(k, initial[k]);
    generations.set(k, 0);
  }

  function assertSlice(slice: keyof S): void {
    if (!values.has(slice)) {
      throw new Error(`store: unknown slice '${String(slice)}' — slices are fixed at createStore()`);
    }
  }

  function scheduleFlush(): void {
    if (flushQueued) return;
    flushQueued = true;
    queueMicrotask(flush);
  }

  function flush(): void {
    flushQueued = false;
    const batch = new Map(pendingPrev);
    pendingPrev.clear();
    if (batch.size === 0) {
      cascadeDepth = 0;
      return;
    }
    for (const [slice, prev] of batch) {
      const current = values.get(slice);
      const subs = subscribers.get(slice);
      if (!subs) continue;
      for (const fn of Array.from(subs)) {
        try {
          fn(current, prev);
        } catch (e) {
          console.error(`store: subscriber for '${String(slice)}' threw`, e);
        }
      }
    }
    if (pendingPrev.size > 0) {
      // Subscribers caused further updates — allow it, but not forever.
      cascadeDepth += 1;
      if (cascadeDepth >= CASCADE_CAP) {
        console.error(
          `store: subscriber cascade exceeded ${CASCADE_CAP} rounds — ` +
          `dropping pending notifications to break the loop. Slices: ` +
          Array.from(pendingPrev.keys()).map(String).join(", "),
        );
        pendingPrev.clear();
        cascadeDepth = 0;
        return;
      }
      scheduleFlush();
    } else {
      cascadeDepth = 0;
    }
  }

  function markChanged(slice: keyof S, prevValue: S[keyof S]): void {
    generations.set(slice, (generations.get(slice) ?? 0) + 1);
    if (!pendingPrev.has(slice)) pendingPrev.set(slice, prevValue);
    scheduleFlush();
  }

  return {
    get(slice) {
      assertSlice(slice);
      return values.get(slice) as any;
    },
    update(slice, patch) {
      assertSlice(slice);
      const current = values.get(slice) as any;
      let changed = false;
      for (const k of Object.keys(patch)) {
        if (!Object.is(current[k], (patch as any)[k])) {
          changed = true;
          break;
        }
      }
      if (!changed) return;
      values.set(slice, { ...current, ...patch });
      markChanged(slice, current);
    },
    set(slice, value) {
      assertSlice(slice);
      const current = values.get(slice) as any;
      if (Object.is(current, value)) return;
      values.set(slice, value);
      markChanged(slice, current);
    },
    subscribe(slice, fn) {
      assertSlice(slice);
      let subs = subscribers.get(slice);
      if (!subs) {
        subs = new Set();
        subscribers.set(slice, subs);
      }
      subs.add(fn as any);
      return () => subs!.delete(fn as any);
    },
    generation(slice) {
      assertSlice(slice);
      return generations.get(slice) ?? 0;
    },
    touch(slice) {
      assertSlice(slice);
      generations.set(slice, (generations.get(slice) ?? 0) + 1);
    },
  };
}

/**
 * The strangler bridge (DESIGN-phase4.md §5): replaces plain fields on a
 * legacy object with accessors into a store slice. Existing `legacy.field`
 * reads/writes keep working unchanged; writes now notify subscribers and
 * bump the generation. Current legacy values seed the slice, so bridging
 * is order-independent of the legacy literal's initialization.
 */
export function bridgeFields<S extends object, K extends keyof S>(
  legacy: any,
  store: Store<S>,
  slice: K,
  fields: (keyof S[K] & string)[],
): void {
  const seed: any = {};
  for (const f of fields) {
    if (f in legacy) seed[f] = legacy[f];
  }
  store.update(slice, seed);
  for (const f of fields) {
    Object.defineProperty(legacy, f, {
      configurable: true,
      enumerable: true,
      get: () => (store.get(slice) as any)[f],
      set: (v) => store.update(slice, { [f]: v } as any),
    });
  }
}
